/**
 * Agent Pages -- Contacts
 *
 * Leads captured by this agent/chatbot.
 */

import React, { useState, useEffect } from 'react'
import { useAgentContext } from '../../../components/layout/AgentContextLayout'
import { Card, CardContent } from '../../../components/ui/Card'
import { Loader2, Mail, Calendar, UserCircle, Inbox } from 'lucide-react'
import { leadsService, type Lead } from '../../../services/leads'

export const ContactsPage: React.FC = () => {
  const { chatbot } = useAgentContext()
  const [leads, setLeads] = useState<Lead[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const fetchLeads = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const data = await leadsService.list(chatbot.id)
        if (!cancelled) setLeads(data.leads ?? [])
      } catch (err: unknown) {
        if (!cancelled) {
          const e = err as { response?: { data?: { detail?: string } } }
          setError(e?.response?.data?.detail ?? 'Failed to load contacts')
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    fetchLeads()
    return () => { cancelled = true }
  }, [chatbot.id])

  const formatDate = (iso?: string) => {
    if (!iso) return '--'
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold tracking-tight text-foreground">Contacts</h1>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Error */}
      {!isLoading && error && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {!isLoading && !error && leads.length === 0 && (
        <Card>
          <CardContent className="py-16 text-center space-y-3">
            <div className="mx-auto w-14 h-14 rounded-full bg-muted flex items-center justify-center">
              <Inbox className="h-7 w-7 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-foreground">No contacts yet</p>
            <p className="text-xs text-muted-foreground max-w-xs mx-auto">
              When end users share their contact information during conversations, they will appear here.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Leads table */}
      {!isLoading && !error && leads.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left font-medium text-muted-foreground px-4 py-3">Name</th>
                    <th className="text-left font-medium text-muted-foreground px-4 py-3">Email</th>
                    <th className="text-left font-medium text-muted-foreground px-4 py-3">Phone</th>
                    <th className="text-left font-medium text-muted-foreground px-4 py-3">Source</th>
                    <th className="text-left font-medium text-muted-foreground px-4 py-3">Captured</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr key={lead.id} className="border-b last:border-b-0 hover:bg-accent/50 transition-colors">
                      <td className="px-4 py-3 flex items-center gap-2">
                        <UserCircle className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <span className="font-medium text-foreground">{lead.name || '--'}</span>
                      </td>
                      <td className="px-4 py-3">
                        {lead.email ? (
                          <span className="flex items-center gap-1.5 text-foreground">
                            <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                            {lead.email}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">--</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-foreground">{lead.phone || '--'}</td>
                      <td className="px-4 py-3 text-muted-foreground">{lead.source || 'widget'}</td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1.5 text-muted-foreground">
                          <Calendar className="h-3.5 w-3.5" />
                          {formatDate(lead.created_at)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
